/**
 * POI Manager Module
 * Handles POI listing with interactive map, clustering, and filtering
 */

import L from 'leaflet';

export default class POIManager {
    constructor(options = {}) {
        this.options = {
            desktopMapId: 'mapEntitiesDesktop',
            mobileMapId: 'mapEntities',
            mapPanelId: 'mapPanel',
            poiDataId: 'poiData',
            filterSelector: '.poi-filter',
            poiItemSelector: '[data-type]',
            defaultCoords: {
                lat: 49.83371965518762,
                lon: 6.005995971233876,
                zoom: 13
            },
            ...options
        };

        this.poiData = [];
        this.desktopMapInstance = null;
        this.mobileMapInstance = null;
        
        this.init();
    }

    /**
     * Initialize the POI manager
     */
    init() {
        this.loadPOIData();
        this.setupEventListeners();
        this.initializeDesktopMap();
        this.applyInitialFilter();
    }

    /**
     * Load POI data from JSON script tag
     */
    loadPOIData() {
        const dataElement = document.getElementById(this.options.poiDataId);
        if (!dataElement) {
            // console.warn('POI data element not found');
            return;
        }

        try {
            this.poiData = JSON.parse(dataElement.textContent);
        } catch (error) {
            // console.error('Failed to parse POI data:', error);
        }
    }

    /**
     * Set up all event listeners
     */
    setupEventListeners() {
        this.setupFilterButtons();
        this.setupMobileMapPanel();
    }

    /**
     * Set up filter button event listeners
     */
    setupFilterButtons() {
        const filterButtons = document.querySelectorAll(this.options.filterSelector);
        filterButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                // Always get the button element, not the clicked target
                const filterValue = button.dataset.filter;
                
                if (filterValue) {
                    this.applyFilter(filterValue);
                    this.updateActiveButton(filterValue);
                    this.updateURL(filterValue);
                }
            });
        });
    }

    /**
     * Set up mobile map panel event listener
     */
    setupMobileMapPanel() {
        const mapPanel = document.getElementById(this.options.mapPanelId);
        if (!mapPanel) return;

        // Handle click events
        mapPanel.addEventListener('click', () => {
            if (!mapPanel.classList.contains('open')) {
                mapPanel.classList.add('open');
                this.initializeMobileMapOnOpen();
            }
        });

        // Use MutationObserver to watch for class changes (from slidePanel.js)
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    if (mapPanel.classList.contains('open') && !this.mobileMapInstance) {
                        this.initializeMobileMapOnOpen();
                    }
                }
            });
        });

        observer.observe(mapPanel, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    /**
     * Initialize mobile map when panel opens
     */
    initializeMobileMapOnOpen() {
        setTimeout(() => {
            if (!this.mobileMapInstance) {
                this.initializeMobileMap();
            }
        }, 400);
    }

    /**
     * Initialize desktop map
     */
    initializeDesktopMap() {
        setTimeout(() => {
            const mapContainer = document.getElementById(this.options.desktopMapId);
            if (mapContainer && mapContainer.offsetWidth > 0) {
                this.desktopMapInstance = this.createMap(this.options.desktopMapId);
                if (this.desktopMapInstance) {
                    this.setupPopupCloseHandler(this.desktopMapInstance.map);
                    this.addMarkersToMap(this.desktopMapInstance);
                }
            } else {
                // Retry if container not ready
                setTimeout(() => this.initializeDesktopMap(), 200);
            }
        }, 100);
    }

    /**
     * Initialize mobile map
     */
    initializeMobileMap() {
        // console.log('Initializing mobile map...');
        this.mobileMapInstance = this.createMap(this.options.mobileMapId);
        if (this.mobileMapInstance) {
            // console.log('Mobile map created successfully');
            this.setupPopupCloseHandler(this.mobileMapInstance.map);
            this.addMarkersToMap(this.mobileMapInstance);
        } else {
            // console.error('Failed to create mobile map instance');
        }
    }

    /**
     * Create a new map instance with clustering
     * @param {string} containerId - Map container ID
     * @returns {Object|null} Map instance with map and markers
     */
    createMap(containerId) {
        if (!window.L) {
            // console.warn('Leaflet not available');
            return null;
        }

        const mapContainer = document.getElementById(containerId);
        if (!mapContainer) {
            // console.warn(`Map container ${containerId} not found`);
            return null;
        }

        // Clean up existing map
        if (mapContainer._leaflet_id) {
            mapContainer._leaflet_id = null;
        }
        mapContainer.innerHTML = '';
        
        // Create map with error handling
        let map;
        try {
            map = L.map(containerId, { zoomControl: false, attributionControl: false })
                .setView([this.options.defaultCoords.lat, this.options.defaultCoords.lon], this.options.defaultCoords.zoom);
        } catch (error) {
            // console.error(`Error creating map for ${containerId}:`, error);
            return null;
        }

        // Fond de plan geoportail.lu (open data ACT). Zoom max 19 : c'est la limite
        // du TileMatrixSet GLOBAL_WEBMERCATOR, pas un choix. Sous-domaines wmts1..4,
        // et pas de suffixe retina — le service n'a pas de tuiles @2x.
        L.tileLayer('https://wmts{s}.geoportail.lu/opendata/wmts/basemap/GLOBAL_WEBMERCATOR/{z}/{x}/{y}.png', {
            subdomains: '1234',
            maxZoom: 19
        }).addTo(map);

        // Create marker cluster group (matching map.js style)
        const markers = L.markerClusterGroup({
            iconCreateFunction: (cluster) => {
                return L.divIcon({
                    html: `<div class="cluster-marker rounded-circle">${cluster.getChildCount()}</div>`,
                    className: 'cluster-icon',
                    iconSize: [26, 26]
                });
            }
        });

        map.addLayer(markers);

        return { map, markers };
    }

    /**
     * Create popup content for POI
     * @param {Object} poi - POI data
     * @returns {string} HTML content for popup
     */
    createPopupContent(poi) {
        const poiUrl = poi.url || `/fr/index-communal/ficheindex/${poi.slug}`;
        
        // Check if POI has a logo/image
        if (poi.logo) {
            return `
                <div class="marker-popup d-flex justify-content-between position-relative">
                    <a href="${poiUrl}" class="stretched-link"></a>
                    <div class="d-flex align-items-baseline justify-content-between w-100 titleContainer">
                        <span class="titleMarker ellipsisone mb-0">${poi.name}</span>
                        <i class="fas fa-chevron-right"></i>
                    </div>
                    <div class="wrapperImg">
                        <img src="${poi.logo}" alt="${poi.name}" class="previewImg w-100 h-100">
                    </div>
                </div>
                <i class="fa-solid fa-circle-xmark closeCross rounded-circle"></i>
            `;
        } else {
            // No image - show title in full width with fixed height
            return `
                <div class="marker-popup position-relative d-flex align-items-center px-3" style="height: 70px;">
                    <a href="${poiUrl}" class="stretched-link"></a>
                    <div class="d-flex align-items-center justify-content-between w-100">
                        <span class="titleMarker ellipsisone mb-0 w-100">${poi.name}</span>
                        <i class="fas fa-chevron-right ms-3"></i>
                    </div>
                </div>
                <i class="fa-solid fa-circle-xmark closeCross rounded-circle"></i>
            `;
        }
    }

    /**
     * Add markers to map based on filter
     * @param {Object} mapInstance - Map instance with map and markers
     * @param {string} filterType - Filter type ('all' or specific type)
     */
    addMarkersToMap(mapInstance, filterType = 'all') {
        if (!mapInstance?.map || !mapInstance?.markers) return;

        const { map, markers } = mapInstance;

        // Clear existing markers
        markers.clearLayers();

        // Filter POIs
        const filteredPois = this.poiData.filter(poi => {
            return poi.latitude && poi.longitude && 
                   (filterType === 'all' || poi.type === filterType);
        });

        // Add markers to cluster
        filteredPois.forEach(poi => {
            const customIcon = L.divIcon({
                className: 'custom-marker rounded-circle',
                iconSize: [15, 15],
                iconAnchor: [7.5, 7.5]
            });

            const marker = L.marker([poi.latitude, poi.longitude], { icon: customIcon });
            
            // Store POI data in marker for later use
            marker.poiData = poi;
            
            // Add click event instead of bindPopup to avoid sizing issues
            marker.on('click', (e) => {
                // Prevent default behavior if originalEvent exists
                if (e.originalEvent) {
                    e.originalEvent.stopPropagation();
                }
                
                // Check if map is ready
                const map = e.target._map;
                if (!map || !map.getContainer()) {
                    // console.warn('Map not ready for popup');
                    return;
                }
                
                // Create and open popup manually
                const popup = L.popup({
                    maxWidth: 300,
                    minWidth: 200,
                    className: 'custom-leaflet-popup',
                    closeButton: true,
                    autoClose: true,
                    closeOnClick: true
                })
                .setLatLng(e.latlng)
                .setContent(this.createPopupContent(poi));
                
                // Small delay to ensure map is ready
                setTimeout(() => {
                    try {
                        popup.openOn(map);
                    } catch (error) {
                        // console.error('Error opening popup:', error);
                    }
                }, 50);
            });

            markers.addLayer(marker);
        });

        // Adjust map view (matching map.js behavior)
        if (markers.getLayers().length > 0) {
            const group = new L.featureGroup(markers.getLayers());
            map.fitBounds(group.getBounds(), { padding: [50, 50] });
        }
    }

    /**
     * Set up popup close handler for map
     * @param {Object} map - Leaflet map instance
     */
    setupPopupCloseHandler(map) {
        map.on('popupopen', () => {
            // Use setTimeout to ensure popup is rendered
            setTimeout(() => {
                const closeButton = document.querySelector('.closeCross');
                if (closeButton) {
                    const closeHandler = (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        map.closePopup();
                        closeButton.removeEventListener('click', closeHandler);
                    };
                    closeButton.addEventListener('click', closeHandler);
                }
            }, 10);
        });
    }

    /**
     * Apply filter to POI list and map
     * @param {string} filterType - Filter type
     */
    applyFilter(filterType) {
        this.filterPOIList(filterType);
        this.updateMapMarkers(filterType);
    }

    /**
     * Filter POI list elements
     * @param {string} filterType - Filter type
     */
    filterPOIList(filterType) {
        // Get all POI items from any container (desktop or mobile)
        const poiItems = document.querySelectorAll(this.options.poiItemSelector);
        
        poiItems.forEach(item => {
            const parentCol = item.closest('.col-12');
            if (!parentCol) return;

            if (filterType === 'all' || item.dataset.type === filterType) {
                parentCol.style.display = '';
                parentCol.style.height = '';
                parentCol.style.margin = '';
                parentCol.style.padding = '';
            } else {
                parentCol.style.display = 'none';
                parentCol.style.height = '0';
                parentCol.style.margin = '0';
                parentCol.style.padding = '0';
            }
        });
    }

    /**
     * Update map markers based on filter
     * @param {string} filterType - Filter type
     */
    updateMapMarkers(filterType) {
        if (this.desktopMapInstance) {
            this.addMarkersToMap(this.desktopMapInstance, filterType);
        }
        if (this.mobileMapInstance) {
            this.addMarkersToMap(this.mobileMapInstance, filterType);
        }
    }

    /**
     * Update active filter button
     * @param {string} activeFilter - Active filter value
     */
    updateActiveButton(activeFilter) {
        const filterButtons = document.querySelectorAll(this.options.filterSelector);
        filterButtons.forEach(btn => {
            if (btn.dataset.filter === activeFilter) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });
    }

    /**
     * Update URL with filter parameter
     * @param {string} type - Filter type
     */
    updateURL(type) {
        const url = new URL(window.location);
        if (type === 'all') {
            url.searchParams.delete('type');
        } else {
            url.searchParams.set('type', type);
        }
        window.history.pushState({}, '', url);
    }

    /**
     * Apply initial filter from URL
     */
    applyInitialFilter() {
        const urlParams = new URLSearchParams(window.location.search);
        const typeParam = urlParams.get('type');
        
        if (typeParam) {
            setTimeout(() => {
                this.applyFilter(typeParam);
                this.updateActiveButton(typeParam);
            }, 200);
        }
    }

    /**
     * Destroy the manager and clean up resources
     */
    destroy() {
        // Remove event listeners
        const filterButtons = document.querySelectorAll(this.options.filterSelector);
        filterButtons.forEach(button => {
            button.removeEventListener('click', this.handleFilterClick);
        });

        // Clean up maps
        if (this.desktopMapInstance?.map) {
            this.desktopMapInstance.map.remove();
        }
        if (this.mobileMapInstance?.map) {
            this.mobileMapInstance.map.remove();
        }

        this.desktopMapInstance = null;
        this.mobileMapInstance = null;
    }

    /**
     * Check if POI manager is available on current page
     */
    static isAvailable() {
        return document.getElementById('poiData') !== null;
    }

    /**
     * Factory method to create instance only if needed
     */
    static createIfNeeded() {
        if (POIManager.isAvailable()) {
            return new POIManager();
        }
        return null;
    }
}